import type { LevelData } from "../types/level";
import type { LevelState } from "../model/LevelState";
import { TILE_SIZE } from "./render-palette";
import { getFogMaskDrawInstructions, type FogMaskDebugMode } from "./draw-model";

export { type FogMaskDebugMode } from "./draw-model";

export class FogRenderer {
  private scene: Phaser.Scene | null = null;
  private maskTexture: Phaser.GameObjects.RenderTexture | null = null;
  private maskHelper: Phaser.GameObjects.Graphics | null = null;
  private bitmapMask: Phaser.Display.Masks.BitmapMask | null = null;
  private debugMode: FogMaskDebugMode = "full";
  private gridWidth: number = 0;
  private gridHeight: number = 0;

  constructor(scene?: Phaser.Scene) {
    if (scene !== undefined) {
      this.scene = scene;
    }
  }

  public initMask(gridWidth: number, gridHeight: number): void {
    if (this.scene === null) return;

    this.gridWidth = gridWidth;
    this.gridHeight = gridHeight;

    const width = gridWidth * TILE_SIZE;
    const height = gridHeight * TILE_SIZE;

    this.maskTexture = this.scene.add.renderTexture(0, 0, width, height);
    this.maskTexture.setOrigin(0, 0);
    this.maskTexture.setDepth(-1);

    const helperConfig: Phaser.Types.GameObjects.Graphics.Options & { add?: boolean } = {
      add: false,
    };
    this.maskHelper = this.scene.make.graphics(helperConfig);

    this.bitmapMask = this.maskTexture.createBitmapMask();
  }

  public render(state: LevelState, data: LevelData): void {
    if (this.maskTexture !== null && this.maskHelper !== null) {
      this.maskTexture.clear();
      this.maskHelper.clear();

      const instructions = getFogMaskDrawInstructions(data.grid, state.fog, this.debugMode);
      for (const inst of instructions) {
        this.maskHelper.fillStyle(0xffffff, inst.alpha);
        this.maskHelper.fillRect(inst.x, inst.y, inst.width, inst.height);
      }
      this.maskTexture.draw(this.maskHelper);
    }
  }

  public getMask(): Phaser.Display.Masks.BitmapMask | null {
    return this.bitmapMask;
  }

  public toggleDebugMaskOpacity(): void {
    this.debugMode = this.debugMode === "full" ? "half" : "full";
  }

  public getDebugMode(): FogMaskDebugMode {
    return this.debugMode;
  }

  public destroy(): void {
    this.maskHelper?.destroy();
    this.maskTexture?.destroy();
    this.bitmapMask?.destroy();
    this.maskHelper = null;
    this.maskTexture = null;
    this.bitmapMask = null;
    this.scene = null;
  }
}
